import { z } from 'zod';
import { type TranslationKey } from '@/i18n/translation-key.tsx';
import { isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';
import { isEventStartAndDurationWithinPlanWindow } from '@/utils/event-create-date-bounds';
import { EVENT_END_TIME_MAX, isStartTimeWithinAllowedWindow } from '@/utils/event-day-time-bounds';

export const createEventSchemaFactory = (t: (key: TranslationKey) => string) => {
  return z
    .object({
      title: z.string().nonempty(t('event.validation.titleNonEmpty')),
      description: z.string().min(2, t('event.validation.descriptionMinLength')).optional(),
      durationDays: z.number().min(1, t('event.validation.durationDaysMinValue')),
      type: z.string().nonempty(t('event.validation.typeRequired')),
      startDate: z.date(),
      multiFlow: z.boolean(),
      role: z.string().optional(),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
    })
    .refine(
      (data) => {
        // startTime is required for 1-day events
        if (data.durationDays === 1) {
          return !!data.startTime;
        }
        return true;
      },
      {
        message: t('event.validation.startTimeRequired'),
        path: ['startTime'],
      },
    )
    .refine(
      (data) => {
        if (data.durationDays !== 1 || !data.startTime) {
          return true;
        }
        return isStartTimeWithinAllowedWindow(data.startTime, data.startDate);
      },
      {
        message: t('event.validation.startTimeOutOfBounds'),
        path: ['startTime'],
      },
    )
    .refine(
      (data) => {
        if (data.durationDays !== 1 || !data.endTime) {
          return true;
        }
        const day = startOfDay(data.startDate);
        const end = parse(data.endTime, 'HH:mm', day);
        const maxEnd = parse(EVENT_END_TIME_MAX, 'HH:mm', day);
        if (!isValid(end) || !isValid(maxEnd)) {
          return false;
        }
        return !isAfter(end, maxEnd);
      },
      {
        message: t('event.validation.endTimeAfterLatestAllowed'),
        path: ['endTime'],
      },
    )
    .refine(
      (data) => {
        if (data.durationDays !== 1 || !data.startTime || !data.endTime) {
          return true;
        }
        const ref = data.startDate;
        const start = parse(data.startTime, 'HH:mm', ref);
        const end = parse(data.endTime, 'HH:mm', ref);
        if (!isValid(start) || !isValid(end)) {
          return false;
        }
        return !isBefore(end, start);
      },
      {
        message: t('event.validation.endTimeBeforeStart'),
        path: ['endTime'],
      },
    )
    .refine((data) => isEventStartAndDurationWithinPlanWindow(data.startDate, data.durationDays), {
      message: t('event.validation.eventBeyondNextYearEnd'),
      path: ['startDate'],
    });
};
